/**
 * SeoLandingPage (Jose 2026-09-16) — renders one Tier-1 keyword landing page from seoLandings.ts.
 * React 19 hoists the <title>/<meta>/<link canonical> we render here into <head> (no helmet dep).
 * Adds a Service + FAQPage JSON-LD block so Google + AI answer engines can rank/quote the intent.
 * Visible, keyworded content + a one-tap Book CTA. SLIK-specific for now (multi-tenant later).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronRight, Phone, MapPin } from 'lucide-react';
import { ShopLogo } from '../components/ShopLogo';
import { SeoLanding, CANONICAL_BASE } from './seoLandings';

const PHONE = '(973) 520-8440';

const SeoLandingPage: React.FC<{ page: SeoLanding }> = ({ page }) => {
  const canonical = `${CANONICAL_BASE}/${page.slug}`;
  const ld = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        name: `${page.serviceName} — SLIK Barber Lounge`,
        serviceType: page.serviceName,
        areaServed: ['Florham Park NJ', 'Madison NJ', 'Chatham NJ', 'East Hanover NJ'],
        provider: {
          '@type': 'BarberShop',
          name: 'SLIK Barber Lounge',
          telephone: '+1-973-520-8440',
          address: {
            '@type': 'PostalAddress', streetAddress: '184 Columbia Turnpike',
            addressLocality: 'Florham Park', addressRegion: 'NJ', postalCode: '07932', addressCountry: 'US',
          },
          url: 'https://slikbarberlounge.com',
        },
        ...(page.price ? { offers: { '@type': 'Offer', price: page.price, priceCurrency: 'USD' } } : {}),
      },
      {
        '@type': 'FAQPage',
        mainEntity: page.faqs.map((f) => ({
          '@type': 'Question', name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      {/* React 19 hoists these into <head> */}
      <title>{page.title}</title>
      <meta name="description" content={page.description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={page.title} />
      <meta property="og:description" content={page.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content="https://book.slikbarberlounge.com/og-image.png" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />

      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ShopLogo className="w-9 h-9 object-contain rounded-lg" />
            <span className="font-bold text-slik-navy">SLIK Barber Lounge</span>
          </Link>
          <Link to="/book" className="inline-flex items-center gap-1.5 bg-slik-purple text-white px-4 py-2 rounded-button font-semibold text-sm hover:opacity-90 transition-all">
            <Calendar className="w-4 h-4" /> Book
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-slik-navy text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
          <h1 className="text-3xl sm:text-5xl font-bold leading-tight mb-3">{page.h1}</h1>
          <p className="text-slik-purple font-semibold mb-5">{page.sub}</p>
          <p className="text-gray-300 max-w-2xl mb-8">{page.intro}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/book" className="inline-flex items-center justify-center gap-2 bg-slik-purple text-white px-7 py-3.5 rounded-button font-bold shadow-button hover:opacity-90 transition-all">
              <Calendar className="w-5 h-5" /> Book Appointment <ChevronRight className="w-5 h-5" />
            </Link>
            <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 bg-white/10 text-white px-7 py-3.5 rounded-button font-bold border border-white/20 hover:bg-white/20 transition-all">
              <Phone className="w-5 h-5" /> Call {PHONE}
            </a>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid sm:grid-cols-2 gap-4">
          {page.bullets.map((b) => (
            <div key={b} className="flex items-start gap-3 p-4 rounded-xl bg-gray-50">
              <span className="mt-1 w-2 h-2 rounded-full bg-slik-purple flex-shrink-0" />
              <span className="text-gray-800 font-medium">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-gray-50 py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-slik-navy mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {page.faqs.map((f) => (
              <div key={f.q}>
                <h3 className="text-lg font-bold text-slik-navy mb-1">{f.q}</h3>
                <p className="text-gray-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer NAP + CTA */}
      <footer className="bg-slik-navy text-gray-300 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-white text-xl font-bold mb-2">SLIK Barber Lounge</h2>
          <p className="flex items-center justify-center gap-2 mb-1"><MapPin className="w-4 h-4" /> 184 Columbia Turnpike, Florham Park, NJ 07932</p>
          <p className="mb-6"><a href={`tel:${PHONE}`} className="hover:text-white">{PHONE}</a></p>
          <Link to="/book" className="inline-flex items-center gap-2 bg-slik-purple text-white px-7 py-3.5 rounded-button font-bold hover:opacity-90 transition-all">
            <Calendar className="w-5 h-5" /> Book your appointment
          </Link>
          <p className="mt-6 text-sm"><Link to="/" className="hover:text-white underline">← Back to SLIK Barber Lounge</Link></p>
        </div>
      </footer>
    </div>
  );
};

export default SeoLandingPage;
